import { getCookie, setCookie, removeCookie } from 'tiny-cookie';
import { AppContextQuery } from '~/graphqlTypes/AppContextQuery';
import { environment } from './environment';

/**
 * The JSON WebToken is given a different name based on the current environment and realm.
 *
 * This is done to avoid having tokens pass between environments.
 */

const getDashboardAuthCookieName = () => ( environment.appMode === 'production' ? 'production-token' : 'staging-token' );
const getAuthCookieName = () => ( environment.appMode === 'production' ? 'ops-production-token' : 'ops-staging-token' );

export const getDashboardToken = () => getCookie( getDashboardAuthCookieName() );

export const getToken = () => getCookie( getAuthCookieName() );

export const setDashboardToken = ( token: string ) => {
  setCookie( getDashboardAuthCookieName(), token, {
    expires: '1M',
    path: '/',
    domain: environment.cookieDomain,
    samesite: 'Lax',
  } );
};

export const setToken = ( token: string ) => {
  setCookie( getAuthCookieName(), token, {
    expires: '1M',
    path: '/',
    domain: environment.cookieDomain,
    samesite: 'Lax',
  } );
};

export const clearDashboardToken = () => {
  removeCookie( getDashboardAuthCookieName(), { path: '/', domain: environment.cookieDomain } );
};

export const clearToken = () => {
  removeCookie( getAuthCookieName(), { path: '/', domain: environment.cookieDomain } );
};

export function gatherAppPermissions( context: AppContextQuery, applicationId: number ) {
  const contextApplication = context.applications.find( ( application ) => application.id === applicationId );
  return contextApplication?.permissions || [];
}

/**
 * This will consume the AppContext and provides a Set<string> of "effective permissions". This is
 * a merge of the user's global permissions and an optional set of application-level permissions.
 */
export function consolidatePermissions( context: AppContextQuery, applicationId?: number ) {
  const globalPerms = context.me.permissions;

  if ( !applicationId ) {
    return new Set( globalPerms );
  }

  const applicationPerms = gatherAppPermissions( context, applicationId );
  return new Set( [...globalPerms, ...applicationPerms] );
}
